/**
 * The truck / driver contact list behind the header window.
 *
 * Read-only here; changing a pairing goes through `useAssignTruckDriver`, which
 * writes the driver row (DriverMaster.TruckRefId) — the one place the pairing is
 * stored.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { queryKeys } from '../../../lib/queryClient';

export interface TruckDriverContact {
  truckId: number;
  truckName: string | null;
  truckNumber: string | null;
  truckType: string | null;
  /** `40FT`, `10TON`, `SPECIAL`… read from truckType; null when unreadable. */
  sizeClass: string | null;
  /** ACTIVE, WORKSHOP or SOLD. */
  truckStatus: string | null;
  /** `YYYY-MM-DD` when a workshop truck is due back. */
  workshopUntil: string | null;
  driverRefId: number | null;
  driverName: string | null;
  driverMobileNo: string | null;
}

const unwrap = (payload: unknown): TruckDriverContact[] => {
  if (Array.isArray(payload)) return payload as TruckDriverContact[];
  const box = payload as { data?: unknown; Data1?: unknown } | null;
  const inner = box?.data ?? box?.Data1;
  return Array.isArray(inner) ? (inner as TruckDriverContact[]) : [];
};

export const truckDriverBookApi = {
  list: async (companyRefId: number): Promise<TruckDriverContact[]> => {
    const { data } = await api.get(API_ENDPOINTS.TRUCK.FLEET_DRIVERS, {
      params: { companyRefId },
    });
    return unwrap(data);
  },

  /**
   * Puts a truck on the road, in the workshop, or marks it sold.
   *
   * Two fields and nothing else: no order, plan or report is touched, and a
   * truck keeps all of its history whichever status it carries. The return date
   * is only meaningful for WORKSHOP; the server clears it otherwise.
   */
  updateStatus: async (
    truckId: number,
    truckStatus: string,
    workshopUntil: string | null,
  ): Promise<void> => {
    await api.put(API_ENDPOINTS.TRUCK.UPDATE_STATUS(truckId), null, {
      params: {
        truckStatus,
        ...(truckStatus === 'WORKSHOP' && workshopUntil ? { workshopUntil } : {}),
      },
    });
  },
};

export const useUpdateTruckStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ truckId, truckStatus, workshopUntil }: {
      truckId: number;
      truckStatus: string;
      workshopUntil: string | null;
    }) => truckDriverBookApi.updateStatus(truckId, truckStatus, workshopUntil),
    onSuccess: () => {
      // The truck list, the truck form and the order calendar all read this.
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks.all });
      queryClient.invalidateQueries({ queryKey: ['truck-order'] });
    },
  });
};

/**
 * The bookable trucks and who drives them.
 *
 * Kept fresh for a minute only: someone else reassigning a driver is exactly
 * the change a person opens this window to see.
 */
export const useFleetDrivers = (companyRefId: number | undefined, enabled = true) =>
  useQuery({
    queryKey: queryKeys.trucks.fleetDrivers(Number(companyRefId) || 0),
    queryFn: () => truckDriverBookApi.list(companyRefId as number),
    enabled: enabled && Boolean(companyRefId),
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
